import {
	getOrganizationRepository,
	getOrgInvitationRepository,
	getCertificateRepository,
} from "@/infrastructure/di";
import type { CreateInvitationRequest } from "@/domain/invitation";
import type { CreateCertificateRequest } from "@/domain/certificate";
import { queryKeys } from "../keys";
import { useCreateMutation } from "../createMutation";

export const useUpdateMemberRole = (orgId: string) =>
	useCreateMutation({
		mutationFn: ({ memberUuid, role }: { memberUuid: string; role: string }) =>
			getOrganizationRepository().updateMemberRole(memberUuid, role),
		invalidateKeys: [queryKeys.organization.users(orgId)],
		errorLogMessage: "Failed to update member role:",
	});

export const useUpdateMemberStatus = (orgId: string) =>
	useCreateMutation({
		mutationFn: ({
			memberUuid,
			is_active,
		}: { memberUuid: string; is_active: boolean }) =>
			getOrganizationRepository().updateMemberStatus(memberUuid, is_active),
		invalidateKeys: [queryKeys.organization.users(orgId)],
		errorLogMessage: "Failed to update member status:",
	});

export const useRemoveMember = (orgId: string) =>
	useCreateMutation({
		mutationFn: async (memberUuid: string) => {
			await getOrganizationRepository().removeMember(memberUuid);
			return memberUuid;
		},
		invalidateKeys: [queryKeys.organization.users(orgId)],
		errorLogMessage: "Failed to remove member:",
	});

export const useOrgResendInvitation = (orgId: string) =>
	useCreateMutation({
		mutationFn: async (invitationUuid: string) => {
			await getOrganizationRepository().resendInvitation(invitationUuid);
			return invitationUuid;
		},
		invalidateKeys: [queryKeys.organization.invitations(orgId)],
		errorLogMessage: "Failed to resend invitation:",
	});

export const useOrgCancelInvitation = (orgId: string) =>
	useCreateMutation({
		mutationFn: async (invitationUuid: string) => {
			await getOrganizationRepository().cancelInvitation(invitationUuid);
			return invitationUuid;
		},
		invalidateKeys: [queryKeys.organization.invitations(orgId)],
		errorLogMessage: "Failed to cancel invitation:",
	});

export const useOrgUpdateOrganization = (orgId: string) =>
	useCreateMutation({
		mutationFn: (data: {
			name?: string;
			email?: string;
			phone?: string;
			website?: string;
			address?: string;
			city?: string;
			state?: string;
			postal_code?: string;
			country?: string;
			fiscal_id?: string;
			tax_advisor_id?: string;
			legal_rep_first_name?: string;
			legal_rep_last_name?: string;
			legal_rep_national_id?: string;
			legal_rep_email?: string;
			is_active?: boolean;
		}) => getOrganizationRepository().updateOrganization(data),
		invalidateKeys: [queryKeys.organization.details(orgId)],
		errorLogMessage: "Failed to update organization:",
	});

export const useOrgCreateInvitation = (orgId: string) =>
	useCreateMutation({
		mutationFn: (data: CreateInvitationRequest) =>
			getOrgInvitationRepository().createInvitation(data),
		invalidateKeys: [queryKeys.organization.invitations(orgId)],
		errorLogMessage: "Failed to create invitation:",
	});

export const useCreateCertificate = (orgId: string) =>
	useCreateMutation({
		mutationFn: (data: CreateCertificateRequest) =>
			getCertificateRepository().createCertificate(data),
		invalidateKeys: [queryKeys.organization.certificates(orgId)],
		errorLogMessage: "Failed to create certificate:",
	});
